/**
 * Formatting utilities for XanBZS ERP
 */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Leading `YYYY-MM-DD` of an encoded-day value \u2014 see {@link encodedDayKey} for
 * what makes a value one.
 */
const ENCODED_DAY_RE = /^\d{4}-\d{2}-\d{2}/;

/** Shared display marker for an unavailable field value. */
export const ABSENT_VALUE = "\u2014";

/**
 * A `YYYY-MM-DD` string as a local-midnight `Date`, or `null` when it is not a
 * real calendar day (`2026-02-30` would otherwise roll over to March 2).
 */
function parseDateOnly(value: string): Date | null {
  const match = DATE_ONLY_RE.exec(value);
  if (!match) {
    return null;
  }

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  const day = Number(match[3]);

  const date = new Date(year, monthIndex, day);
  if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day) {
    return null;
  }

  return date;
}

function parseDateValue(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (DATE_ONLY_RE.test(trimmed)) {
    return parseDateOnly(trimmed);
  }

  const date = new Date(trimmed);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date;
}

/**
 * The `YYYY-MM-DD` key of a `Date`'s local calendar day.
 *
 * Date-only columns and `<input type="date">` values carry no timezone, so the
 * day has to come from local calendar parts. `date.toISOString()` is UTC: from
 * 17:00 PDT / 20:00 EDT onward it already reports tomorrow, which books an
 * evening document on the wrong day — and in the wrong month at a month
 * boundary, where the finance dashboard's local-date windows then miss it.
 * East of Greenwich the same projection runs the other way, turning a local
 * 1 March into `2026-02-28`.
 *
 * This is the one supported conversion; `format.date-only-defaults.test.ts`
 * keeps the UTC spellings out.
 */
export function localDayKey(date: Date): string {
  // The year is padded like the month and day. `getFullYear()` is not
  // zero-padded, so a sub-1000 year — `0205` is a plausible typo for `2025` —
  // yields three characters, and `monthKey`'s fixed `slice(0, 7)` then cuts
  // mid-field into `"205-03-"`. Every field fixed-width keeps the key both
  // lexicographically sortable and safe to slice.
  const year = String(date.getFullYear()).padStart(4, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Today's calendar day in the operator's timezone as `YYYY-MM-DD`. */
export function todayDateOnly(): string {
  return localDayKey(new Date());
}

/**
 * A `YYYY-MM-DD` day key as a local-midnight `Date` — the inverse of
 * {@link localDayKey} — or `null` when the value is not exactly one real
 * calendar day.
 *
 * Grid and range math needs to go back the other way from a key, and reads
 * that must round-trip through the same calendar `localDayKey` writes.
 * `new Date("2026-03-14")` parses date-only strings as *UTC* midnight, so west
 * of UTC it lands on 13 March and a month grid built from it opens a day early.
 * Rejecting `2026-02-30` rather than rolling it into March keeps a malformed
 * URL from quietly showing the wrong month.
 */
export function parseDayKey(value: string | null | undefined): Date | null {
  const trimmed = value?.trim() ?? "";
  return trimmed ? parseDateOnly(trimmed) : null;
}

/** Format a number as USD currency */
export function formatCurrency(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) {
    return "$0.00";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
}

/** Format a date string with time */
export function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return ABSENT_VALUE;
  }

  const date = parseDateValue(dateStr);
  if (!date) {
    return ABSENT_VALUE;
  }
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Format a number compactly (e.g. $12.3K, $1.2M) */
export function formatCompact(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) {
    return "$0";
  }

  const sign = amount < 0 ? "-" : "";
  const absAmount = Math.abs(amount);

  if (absAmount >= 1_000_000) {
    return `${sign}$${(absAmount / 1_000_000).toFixed(1)}M`;
  }
  if (absAmount >= 1000) {
    const inThousands = absAmount / 1000;
    const thousandsRounded = Number(inThousands.toFixed(1));

    if (thousandsRounded >= 1000) {
      return `${sign}$${(thousandsRounded / 1000).toFixed(1)}M`;
    }

    return `${sign}$${thousandsRounded.toFixed(1)}K`;
  }
  return formatCurrency(amount);
}

/** Format a date string nicely */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return ABSENT_VALUE;
  }

  const date = parseDateValue(dateStr);
  if (!date) {
    return ABSENT_VALUE;
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Calendar day a date value falls on, as `YYYY-MM-DD`, or `null` if unparseable.
 *
 * Shares `parseDateValue` with `formatDate` on purpose, so anything that orders
 * or buckets by this agrees with the date the operator actually sees. Slicing
 * the raw string instead reads a timestamp in UTC while `formatDate` prints the
 * local day, and `2026-03-01T02:30:00Z` renders "Feb 28, 2026" anywhere west of
 * UTC. An unparseable value returns `null` rather than a plausible-looking
 * prefix, so callers can put it in the same bucket they sort it into.
 */
export function dayKey(dateStr: string | null | undefined): string | null {
  if (!dateStr) {
    return null;
  }

  const date = parseDateValue(dateStr);
  if (!date) {
    return null;
  }
  return localDayKey(date);
}

/**
 * Calendar month a date value belongs to, as `YYYY-MM`, or `null` if
 * unparseable. Same calendar as {@link dayKey} and `formatDate`.
 */
export function monthKey(dateStr: string | null | undefined): string | null {
  return dayKey(dateStr)?.slice(0, 7) ?? null;
}

/**
 * The calendar day an *encoded day* names, as a local-midnight `Date`, or
 * `null` when the value is missing or not a real calendar day.
 *
 * An encoded day is a date-only value a writer serialized into a timestamp
 * column at UTC midnight: `tasks.due_date`, `tasks.start_date`, and
 * `projects.due_date` all go through `normalizeTimestampInput`, which stores
 * the picked `"2026-01-16"` as `"2026-01-16T00:00:00.000Z"`. The leading
 * `YYYY-MM-DD` is therefore the day the operator picked, and the `T00:00:00Z`
 * suffix carries no information — the forms round-trip the same prefix back
 * into their date inputs via `value.split("T")[0]`.
 *
 * Read such a value as an instant ({@link dayKey}, {@link formatDate}, or
 * `new Date(value)`) and you get UTC midnight, which is the *previous*
 * calendar day everywhere west of UTC — so the day buckets, keys, and renders
 * one early. Taking the prefix and re-anchoring it at local midnight recovers
 * the encoded day in any timezone.
 *
 * Use this only for columns written in that convention. A genuine instant
 * (`created_at`, `completed_at`) belongs to {@link dayKey}, which reads it in
 * the operator's timezone.
 */
export function parseEncodedDay(value: string | null | undefined): Date | null {
  const match = ENCODED_DAY_RE.exec(value?.trim() ?? "");
  return match ? parseDateOnly(match[0]) : null;
}

/**
 * The `YYYY-MM-DD` key of an encoded day (see {@link parseEncodedDay}), or
 * `null`. Same shape and local calendar as {@link dayKey}, so keys from either
 * agree with a grid built from local `Date` parts.
 */
export function encodedDayKey(value: string | null | undefined): string | null {
  const day = parseEncodedDay(value);
  return day ? localDayKey(day) : null;
}

/**
 * Calendar month an encoded day belongs to, as `YYYY-MM`, or `null` if
 * unparseable. Same calendar as {@link encodedDayKey} and
 * {@link formatEncodedDay} — a bucket built from this agrees with the day
 * those two render, where {@link monthKey} (the instant reader) would not:
 * `2026-03-01T00:00:00Z` is the encoded day 1 March everywhere, but reads as
 * the instant 28 February anywhere west of UTC.
 */
export function encodedMonthKey(value: string | null | undefined): string | null {
  return encodedDayKey(value)?.slice(0, 7) ?? null;
}

/**
 * An encoded day rendered on its own calendar day (see
 * {@link parseEncodedDay}).
 *
 * Delegates to {@link formatDate} for a single rendering style and its absent
 * marker, passing the date-only key so it renders the operator's day rather
 * than the UTC instant.
 */
export function formatEncodedDay(value: string | null | undefined): string {
  return formatDate(encodedDayKey(value));
}

/**
 * An encoded day in the compact `M/D/YY` style the board cards use.
 *
 * Same calendar as {@link formatEncodedDay}; kept separate because a kanban
 * card cannot afford the long form.
 */
export function formatEncodedShortDay(value: string | null | undefined): string {
  const parsed = parseEncodedDay(value);
  return parsed
    ? parsed.toLocaleDateString("en-US", { day: "numeric", month: "numeric", year: "2-digit" })
    : ABSENT_VALUE;
}

/**
 * A local calendar day written back out as an encoded day (see
 * {@link parseEncodedDay}) — the inverse of {@link encodedDayKey}.
 *
 * A range bound compared against an encoded-day column has to be encoded the
 * same way. `date.toISOString()` encodes the local *instant* instead, so west
 * of UTC a month's first day lands after its own window's start and the whole
 * day drops out of the query.
 */
export function toEncodedDay(date: Date): string {
  return `${localDayKey(date)}T00:00:00.000Z`;
}

/** Format a date as relative time (e.g. "2 days ago") */
export function formatRelative(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return ABSENT_VALUE;
  }

  const date = parseDateValue(dateStr);
  if (!date) {
    return ABSENT_VALUE;
  }
  const now = new Date();

  const diffMs = now.getTime() - date.getTime();
  const isFuture = diffMs < 0;

  const mins = Math.floor(Math.abs(diffMs) / 60_000);
  if (mins < 1) {
    return isFuture ? "soon" : "just now";
  }
  if (mins < 60) {
    return isFuture ? `in ${mins}m` : `${mins}m ago`;
  }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return isFuture ? `in ${hrs}h` : `${hrs}h ago`;
  }
  const days = Math.floor(hrs / 24);
  if (days < 7) {
    return isFuture ? `in ${days}d` : `${days}d ago`;
  }
  return formatDate(dateStr);
}

/** Capitalize first letter */
export function capitalize(s: string | null | undefined): string {
  if (!s) {
    return ABSENT_VALUE;
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Fall back to "N/A" for an empty, null, or undefined string field. */
export function orNA(value: string | null | undefined): string {
  return value || "N/A";
}

/**
 * Fall back to `ABSENT_VALUE` for an empty, null, or undefined string field.
 *
 * The canonical marker for "no value here". Prefer this over an inline
 * `value || ABSENT_VALUE`: a row that repeats that fallback once per cell turns
 * a table row into a branch-heavy function for no reader benefit, and the
 * repetition is how "Not provided" / "Not available" / "---" / "N/A" drifted
 * into four competing markers in the first place.
 */
export function orAbsent(value: string | null | undefined): string {
  return value || ABSENT_VALUE;
}

/**
 * The one way a document (invoice, estimate, purchase order, payment, credit,
 * shipment) is referenced in the UI: its business `uid`, or the first eight
 * characters of its row id while a legacy row has no uid yet.
 */
export function formatDocumentUid(
  uid: string | null | undefined,
  id: string | null | undefined,
): string {
  return uid || id?.slice(0, 8) || "";
}
